import React, { useEffect, useRef, useState } from "react";
import StoreFinder from "@src/stories/ecommerce/StoreFinder/StoreFinder";
import useLocationSearch from "@src/hooks/useLocationSearch";
import arcBridge from "frontend/js/arcBridge";

const StoreSelectModal = ({ isOpen, onClose, onSelectStore, selectedStore }) => {
  const inputRef = useRef(null);
  const [clickAndCollectStores, setClickAndCollectStores] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    stores: searchResults,
    searchByQuery,
    loading: searchLoading,
    hasSearched,
  } = useLocationSearch(inputRef);

  const hasClickAndCollectService = (store) => {
    return store.store_services?.some(service => 
      service.service_label && (
        service.service_label.toLowerCase().includes('click & collect available') ||
        service.service_label.toLowerCase().includes('click and collect available') ||
        service.service_label.toLowerCase().includes('click&collect available')
      )
    ) || false;
  };

  const fetchClickAndCollectStores = async () => {
    setLoading(true);
    try {
      const response = await arcBridge.getLocations({ initialLoad: true });
      if (response?.stores?.length > 0) {
        const ccStores = response.stores.filter(hasClickAndCollectService);
        
        // Define proper state/territory order for Australia
        const stateOrder = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];
        
        const sortedStores = ccStores.sort((a, b) => {
          const stateA = (a.state || '').toUpperCase();
          const stateB = (b.state || '').toUpperCase();
          
          const stateIndexA = stateOrder.indexOf(stateA);
          const stateIndexB = stateOrder.indexOf(stateB);

          if (stateIndexA !== -1 && stateIndexB !== -1) {
            if (stateIndexA !== stateIndexB) return stateIndexA - stateIndexB;
          }
          else {
            const stateCompare = stateA.localeCompare(stateB);
            if (stateCompare !== 0) return stateCompare;
          }

          return (a.name || '').localeCompare(b.name || '');
        });
        
        setClickAndCollectStores(sortedStores);
      } else {
        setClickAndCollectStores([]);
      }
    } catch (error) {
      console.error("Error fetching Click & Collect stores:", error);
      setClickAndCollectStores([]);
    } finally {
      setLoading(false);
    }
  };

  const groupStoresByState = (stores: any[]): Record<string, any[]> => {
    return stores.reduce((groups: Record<string, any[]>, store: any) => {
      const state = store.state || 'Unknown';
      if (!groups[state]) {
        groups[state] = [];
      }
      groups[state].push(store);
      return groups;
    }, {});
  };

useEffect(() => {
  if (isOpen) {
    window.scrollTo({ top: 0, behavior: "instant" });
    fetchClickAndCollectStores(); // Fetch C&C stores instead of search
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }

  return () => {
    document.body.style.overflow = "";
  };
}, [isOpen]);

  return isOpen ? (
    <div className="relative z-20">
      <div className="fixed h-screen inset-0 bg-white lg:inset-auto lg:absolute lg:left-[90px] lg:top-full lg:h-[360px] lg:w-[474px] lg:rounded-none lg:border lg:border-[#BBBBBD] lg:shadow-[0px_4px_11px_0px_rgba(84,86,90,0.10)]">
        <button
          className="justify-end border-b p-4 border-[#ECEEEF] h-[58px] lg:hidden"
          onClick={() => {
            document.body.classList.remove("modal-open");
            onClose();
          }}
          aria-label="Close"
        >
          <i className="flex items-center icon icon-close-rounded"></i>
        </button>
        <div className="h-full p-4 overflow-y-auto bg-white lg:p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-[#54565A] text-[21px]/6">Set My Store</h4>
          </div>
          {loading ? (
            <div className="text-center text-[#54565A] blabel py-6">Loading stores...</div>
          ) : (
            <>
              <StoreFinder
                inputRef={inputRef}
                layout="default"
                onSearch={searchByQuery}
              />
              {searchLoading && (
                <div className="text-center text-[#54565A] blabel py-6">Searching stores...</div>
              )}
              {/* Show search results if user has searched, otherwise show all C&C stores */}
              {!searchLoading && (
                <>
                  {hasSearched && searchResults?.length > 0 && (
                    <div className="mt-6">
                      <ul className="p-0 space-y-3">
                        {searchResults.filter(hasClickAndCollectService).map((store: any) => (
                          <li
                            key={store.id}
                            onClick={() => {
                              onSelectStore(store);
                              onClose();
                            }}
                            className="flex items-center justify-between border-b border-[#ECEEEF] last:border-b-transparent cursor-pointer pb-3"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-5 h-5 border-2 border-[#23263B] rounded-full flex items-center justify-center">
                                {selectedStore?.id === store.id && (
                                  <div className="w-2.5 h-2.5 bg-[#23263B] rounded-full"></div>
                                )}
                              </div>
                              <span className="font-normal text-black truncate b2">
                                {store.name}
                              </span>
                            </div>
                            {store.distance && (
                              <div className="blabel text-[#54565A] font-medium flex items-center">
                                {store.distance} km
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {hasSearched && searchResults?.filter(hasClickAndCollectService).length === 0 && (
                    <div className="text-center text-[#54565A] blabel py-6">
                      No Click & Collect stores found in this area.
                    </div>
                  )}
                  {!hasSearched && clickAndCollectStores?.length > 0 && (
                    <div className="mt-6">
                      <ul className="p-0 space-y-3">
                        {Object.entries(groupStoresByState(clickAndCollectStores)).map(([state, stores]) =>
                          (stores as any[]).map((store: any) => (
                            <li
                              key={store.id}
                              onClick={() => {
                                onSelectStore(store);
                              onClose();
                            }}
                            className="flex items-center justify-between border-b border-[#ECEEEF] last:border-b-transparent cursor-pointer pb-3"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-5 h-5 border-2 border-[#23263B] rounded-full flex items-center justify-center">
                                {selectedStore?.id === store.id && (
                                  <div className="w-2.5 h-2.5 bg-[#23263B] rounded-full"></div>
                                )}
                              </div>
                              <span className="font-normal text-black truncate b2">
                                {store.name}
                              </span>
                            </div>
                            {store.distance && (
                              <div className="blabel text-[#54565A] font-medium flex items-center">
                                {store.distance} km
                              </div>
                            )}
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  )}
                  {!hasSearched && clickAndCollectStores?.length === 0 && !loading && (
                    <div className="text-center text-[#54565A] blabel py-6">
                      No Click & Collect stores available.
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  ) : null;
};

export default StoreSelectModal;